import { useLocation, useNavigate } from 'react-router-dom';
import AppearanceSettings from '../components/AppearanceSettings';
import WindsorConnect from '../components/WindsorConnect';
import MemberManagement from '../components/MemberManagement';
import JarvisSettings from '../components/JarvisSettings';
import AgentKeys from '../components/AgentKeys';
import Markdown from '../components/Markdown';
// CHANGELOG.md at the repo root is the source: whoever ships a change adds a line in the same commit.
import changelogMd from '../../../CHANGELOG.md?raw';
import { OperationalReadiness } from "../components/OperationalReadiness";
import ClientLinksNote from "../components/ClientLinksNote";
import BrightLocalConnect from "../components/BrightLocalConnect";
import FilterPill from "../components/FilterPill";
import { Binoculars, LibraryBig, ScrollText,
  Palette,
  Activity,
  BadgeCheck,
  BarChart3,
  Bot,
  Check,
  ChevronDown,
  CircleGauge,
  Database,
  FolderOpen,
  LockKeyhole,
  Mail,
  MapPin,
  Megaphone,
  MessageSquare,
  Mic2,
  NotebookPen,
  Phone,
  KeyRound,
  Plug,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Route,
  Trash2,
  UserRound,
  WandSparkles,
  Workflow,
  Share2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { AGENCY_TZ, api, formatDateTime } from "../api";
import { roleLabel } from "../roles";
import {
  GoogleConnect,
  MyGoogle,
  SendingDomain,
  GoogleReturnNotice,
} from "../components/IntegrationSetup";
import { useAuth } from "../auth";
import GoogleAdsAccounts from "../components/GoogleAdsAccounts";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/Ui";
import { sentence } from "../components/Workflow";
import { useApi } from "../hooks";

type Tone = "neutral" | "success" | "warning" | "danger" | "info";
type Loaded<T> = { data: T | null; loading: boolean; error: string | null; reload: () => void };

type Operations = {
  jobs: Array<{
    id: number;
    provider: string;
    status: string;
    error_code: string | null;
    created_at?: string | null;
    finished_at?: string | null;
  }>;
  workers: Array<{ name: string; last_finished_at: string | null; status: string }>;
  scheduler_configured: boolean;
};

// The three background workers, by what they do.
const WORKER_LABEL: Record<string, string> = { webhooks: "Webhooks", syncs: "Data syncs", health: "Health scores" };
// Where a queued event came from.
const JOB_SOURCE: Record<string, string> = {
  ctm: "CallTrackingMetrics",
  ghl: "GoHighLevel",
  google_leadform: "Google lead form",
  fireflies: "Fireflies.ai",
  readai: "Read.ai",
};
const JOB_PROBLEM: Record<string, string> = {
  processing_failed: "Could not be processed",
  interrupted_review_required: "Stopped partway. Check it before retrying",
};
// Only database-only work is retried from here (routes/operations.js); AI jobs need a look first.
const RETRYABLE = ["ctm", "ghl", "google_leadform"];

function workerState(status: string): { tone: Tone; text: string } {
  if (status === "ok") return { tone: "success", text: "OK" };
  if (status === "failed") return { tone: "danger", text: "Failed" };
  if (status === "running") return { tone: "info", text: "Running" };
  return { tone: "neutral", text: sentence(status) };
}

function WorkerStatus({ request }: { request: Loaded<Operations> }) {
  const [notice, setNotice] = useState("");
  const [retrying, setRetrying] = useState<number | null>(null);
  const jobs = request.data?.jobs || [];
  const failed = jobs.filter((job) => job.status === "failed");
  const others = jobs.filter((job) => job.status !== "failed");
  const source = (provider: string) => JOB_SOURCE[provider] || sentence(provider);
  async function retry(id: number) {
    setRetrying(id);
    setNotice("");
    try {
      await api(`/api/operations/${id}/retry`, { method: "POST" });
      request.reload();
    } catch (e) {
      setNotice(e instanceof Error ? e.message : "Retry failed");
    } finally {
      setRetrying(null);
    }
  }
  return (
    <section className="panel settings-workers">
      <div className="panel-heading">
        <div>
          <h2>Worker activity</h2>
          <p>Background jobs and when they last finished.</p>
        </div>
        <button className="icon-button" aria-label="Refresh worker activity" onClick={request.reload}>
          <RefreshCw aria-hidden="true" />
        </button>
      </div>
      {request.error ? (
        <ErrorState title="Worker activity is unavailable" message={request.error} onRetry={request.reload} />
      ) : !request.data ? (
        <LoadingState label="Loading worker activity" />
      ) : (
        <>
          {request.data.workers.length ? (
            <ul className="settings-status-list">
              {request.data.workers.map((worker) => {
                const state = workerState(worker.status);
                return (
                  <li key={worker.name}>
                    <span className="settings-status-name">{WORKER_LABEL[worker.name] || sentence(worker.name)}</span>
                    <span className="settings-status-value"><StatusBadge tone={state.tone}>{state.text}</StatusBadge></span>
                    <span className="settings-status-detail">
                      {worker.last_finished_at ? `Last run ${formatDateTime(worker.last_finished_at)}` : "No finished run yet"}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="settings-panel-note">No background job has finished yet.</p>
          )}
          {failed.length > 0 && (
            <div className="settings-jobs">
              <h3>Failed events ({failed.length})</h3>
              <div className="table-wrap">
                <table className="settings-table">
                  <thead><tr><th>Event</th><th>Received</th><th>Problem</th><th><span className="sr-only">Retry</span></th></tr></thead>
                  <tbody>
                    {failed.map((job) => (
                      <tr key={job.id}>
                        <td>{source(job.provider)}</td>
                        <td>{formatDateTime(job.created_at)}</td>
                        <td>{job.error_code ? JOB_PROBLEM[job.error_code] || sentence(job.error_code) : "Failed"}</td>
                        <td className="settings-table-action">
                          {RETRYABLE.includes(job.provider) && (
                            <button className="button secondary" disabled={retrying !== null} onClick={() => void retry(job.id)}>
                              {retrying === job.id ? "Queuing…" : "Queue retry"}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {others.length > 0 && (
            <details className="settings-jobs">
              <summary>Recent events ({others.length})</summary>
              <div className="table-wrap">
                <table className="settings-table">
                  <thead><tr><th>Event</th><th>Received</th><th>Status</th></tr></thead>
                  <tbody>
                    {others.map((job) => (
                      <tr key={job.id}>
                        <td>{source(job.provider)}</td>
                        <td>{formatDateTime(job.created_at)}</td>
                        <td>{sentence(job.status)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          {notice && <p className="inline-notice danger settings-panel-alert" role="alert">{notice}</p>}
        </>
      )}
    </section>
  );
}

type HealthResponse = {
  status: "ok" | "unavailable" | "configuration_required";
  database?: "connected" | "unreachable";
  background_jobs?: "disabled" | "standalone";
};

type CredentialField = {
  key: string;
  label: string;
  placeholder: string;
  secret: boolean;
  required: boolean;
  configured: boolean;
  source: "vault" | "environment" | null;
};

type LastTest = {
  outcome: "success" | "failed";
  at: string;
  detail: string | null;
  /** Its keys or the Google authorization changed after this test. */
  changed_since: boolean;
};

type Integration = {
  id: string;
  name: string;
  category: string;
  icon: string;
  description: string;
  testable: boolean;
  fields: CredentialField[];
  configured: boolean;
  details: Record<string, string | number | boolean | null>;
  last_test?: LastTest | null;
};

type IntegrationResponse = {
  providers: Integration[];
  configured: number;
  total: number;
  vault_ready: boolean;
};

type CredentialEvent = {
  id: number;
  provider: string;
  credential_key: string | null;
  action: "configured" | "replaced" | "removed" | "tested";
  outcome: "success" | "failed";
  detail: string | null;
  occurred_at: string;
  actor_name: string | null;
  client_id?: number | null;
  client_name?: string | null;
};

type SettingsSection = "appearance" | "overview" | "integrations" | "jarvis" | "agent" | "members" | "security" | "activity" | "changelog";
const SECTIONS: SettingsSection[] = ["overview", "appearance", "integrations", "jarvis", "agent", "members", "security", "activity", "changelog"];

// One icon per service (routes/settings.js `icon`). The plug is the Integrations menu's own icon.
const iconMap: Record<string, ComponentType<{ "aria-hidden"?: boolean }>> = {
  sparkles: Sparkles,
  route: Route,
  bot: Bot,
  wand: WandSparkles,
  drive: FolderOpen,
  chart: BarChart3,
  message: MessageSquare,
  phone: Phone,
  workflow: Workflow,
  map: MapPin,
  mic: Mic2,
  mail: Mail,
  megaphone: Megaphone,
  search: Binoculars,
  library: LibraryBig,
  badge: BadgeCheck,
  notebook: NotebookPen,
  share: Share2,
};

const sectionCopy: Record<
  SettingsSection,
  { title: string; description?: string }
> = {
  // Titles only, except where one line says what the list holds.
  appearance: { title: "Appearance" },
  overview: { title: "Workspace settings" },
  integrations: { title: "Integrations" },
  jarvis: { title: "Jarvis", description: "Which AI the Jarvis assistant runs on." },
  agent: { title: "Agent API", description: "Let your own Claude or ChatGPT read NOLA for you, read only." },
  members: { title: "Members" },
  security: {
    title: "System & security",
    description: "The database, saved keys and background jobs.",
  },
  activity: {
    title: "Activity",
    description: "When keys were saved, tested, replaced or removed.",
  },
  changelog: { title: "Changelog", description: "What changed in NOLA, newest first." },
};

/** The changelog from its first dated heading on: the section supplies the title and intro line. */
const changelogText = changelogMd.slice(Math.max(0, changelogMd.indexOf("\n## ") + 1));

/** "#my-google": this person's own Gmail and Calendar card (the account menu and Integrations link to it). */
const MY_GOOGLE = "my-google";

/** "#integrations/slack" → the Integrations section with the Slack card open. */
function parseHash(hash: string): { section: SettingsSection; focus: string | null } {
  const [name, focus] = hash.replace("#", "").split("/");
  // The card sits on the overview.
  if (name === MY_GOOGLE) return { section: "overview", focus: null };
  return SECTIONS.includes(name as SettingsSection)
    ? { section: name as SettingsSection, focus: name === "integrations" && focus ? focus : null }
    : { section: "overview", focus: null };
}

/** On #my-google, bring the person's Gmail and Calendar card into view once it has loaded. */
function useScrollToMyGoogle(hash: string) {
  useEffect(() => {
    if (hash !== `#${MY_GOOGLE}`) return;
    let tries = 0;
    let timer = 0;
    const go = () => {
      const card = document.getElementById("my-google-title")?.closest("section");
      if (card) card.scrollIntoView({ block: "start" });
      else if (tries++ < 30) timer = window.setTimeout(go, 100);
    };
    // After the layout's own scroll to the top on a new page, not before it.
    timer = window.setTimeout(go, 50);
    return () => window.clearTimeout(timer);
  }, [hash]);
}

/**
 * Integrations' pointer to "Your Gmail and Calendar": Gmail tasks and booking calendars
 * are each person's own Google account, not an agency key in the table below.
 */
function MyGoogleRow({ onOpen }: { onOpen: () => void }) {
  const status = useApi<{ connected: boolean; email: string | null }>("/api/me/google");
  const yours = status.data
    ? status.data.connected ? `Yours is connected${status.data.email ? ` as ${status.data.email}` : ""}.` : "Yours is not connected yet."
    : "";
  return (
    <section className="panel settings-account-row" aria-label="Gmail and Calendar">
      <span className="settings-icon"><Mail aria-hidden="true" /></span>
      <div>
        <h2>Gmail and Calendar</h2>
        <p>Each person connects their own Google account for their work queue and booking pages. {yours}</p>
      </div>
      {/* Named for what it does, like the Overview's button for the same card. */}
      <button type="button" className="button secondary" onClick={onOpen}>{status.data && !status.data.connected ? "Connect Google" : "Manage"}</button>
    </section>
  );
}

/**
 * The one status a service shows. A failed last test outranks everything, unless its keys
 * (or the Google authorization) changed after it; then whether its keys are saved.
 * `rank` orders the overview: what needs attention first.
 */
function integrationState(provider: Integration): { tone: Tone; text: string; rank: number } {
  const test = provider.last_test;
  if (provider.configured && test?.outcome === "failed" && !test.changed_since)
    return { tone: "danger", text: "Last test failed", rank: 0 };
  if (!provider.configured) return { tone: "neutral", text: "Setup needed", rank: 1 };
  return { tone: "success", text: "Configured", rank: 2 };
}

/** Integrations table: the categories in the order an agency reaches for them; any new one goes last. */
const CATEGORY_ORDER = ["Google", "AI", "Calls & CRM", "Reporting", "Social", "Local SEO", "Messaging", "Automation", "Email"];
const categoryRank = (category: string) => {
  const at = CATEGORY_ORDER.indexOf(category);
  return at < 0 ? CATEGORY_ORDER.length : at;
};

/** "Sep 30, 3:02 PM ET" this year, "Sep 30, 2025, 3:02 PM ET" before: one moment, short, on the agency's clock. */
function shortWhen(value: string | null | undefined) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = (d: Date) => new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone: AGENCY_TZ }).format(d);
  const sameYear = year(date) === year(new Date());
  return `${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" as const }), hour: "numeric", minute: "2-digit", timeZone: AGENCY_TZ }).format(date)} ET`;
}

// Field names inside a sentence: "Needs access key and secret key", but "API key" and "Resend API key" keep their capitals.
const NAMED = /^(Resend|Gemini|Google|Slack|Windsor|Fireflies)\b/;
const inSentence = (label: string) => (NAMED.test(label) || /^.[A-Z]/.test(label) ? label : label.charAt(0).toLowerCase() + label.slice(1));
const joinAnd = (items: string[]) => (items.length <= 2 ? items.join(" and ") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);

/** The Detail column: the last test, else what is connected, else what setup still needs. */
export function integrationDetail(provider: Integration): string {
  const test = provider.last_test;
  if (provider.configured && test) {
    if (test.outcome === "failed" && !test.changed_since) return `Test failed ${shortWhen(test.at)}`;
    if (test.outcome === "failed") return "Changed since the last test failed";
    return `Test passed ${shortWhen(test.at)}`;
  }
  const tokens = Number(provider.details.connected || 0);
  if (provider.id === "ghl" && tokens > 0) return `${tokens} client ${tokens === 1 ? "token" : "tokens"} saved`;
  if (Boolean(provider.details.oauth_connected))
    return provider.details.account ? `Authorized as ${String(provider.details.account)}` : "Google authorization saved";
  if (provider.id === "lsa")
    return provider.details.scope_missing ? "Google Workspace needs reauthorizing" : "Uses the Google Workspace connection";
  if (provider.configured) return provider.testable ? "Not tested yet" : "Keys saved";
  const needed = provider.fields.filter((field) => field.required && !field.configured).map((field) => inSentence(field.label));
  return needed.length ? `Needs ${joinAnd(needed)}` : "Nothing saved yet";
}

/** The last test, in one line inside the open row. */
function LastTestLine({ test }: { test: LastTest }) {
  if (test.outcome === "failed" && !test.changed_since)
    return (
      <p className="integration-last-test failed">
        Last test failed {formatDateTime(test.at)}{test.detail ? `: ${test.detail}` : "."}
      </p>
    );
  if (test.outcome === "failed")
    return <p className="integration-last-test">Changed since the last test failed on {formatDateTime(test.at)}. Test the connection to check it.</p>;
  return <p className="integration-last-test">Last test passed {formatDateTime(test.at)}.</p>;
}

function SettingsRail({
  active,
  onChange,
  configured,
  total,
}: {
  active: SettingsSection;
  onChange: (section: SettingsSection) => void;
  configured: number;
  total: number;
}) {
  const entries: {
    section: SettingsSection;
    label: string;
    icon: ComponentType<{ "aria-hidden"?: boolean }>;
    meta?: string;
  }[] = [
    { section: "overview", label: "Overview", icon: CircleGauge },
    { section: "appearance", label: "Appearance", icon: Palette },
    {
      section: "integrations",
      label: "Integrations",
      icon: Plug,
      meta: total ? `${configured}/${total}` : undefined,
    },
    { section: "jarvis", label: "Jarvis", icon: Bot },
    { section: "agent", label: "Agent API", icon: KeyRound },
    { section: "members", label: "Members", icon: UserRound },
    { section: "security", label: "System & security", icon: ShieldCheck },
    { section: "activity", label: "Activity", icon: Activity },
    { section: "changelog", label: "Changelog", icon: ScrollText },
  ];
  return (
    <nav className="settings-rail" aria-label="Settings sections">
      <label className="settings-mobile-select"><span>Settings section</span><select value={active} onChange={e=>onChange(e.target.value as SettingsSection)}>{entries.map(entry=><option key={entry.section} value={entry.section}>{entry.label}</option>)}</select></label>
      {entries.map((entry) => {
        const Icon = entry.icon;
        return (
          <button
            key={entry.section}
            aria-current={active === entry.section ? "page" : undefined}
            className={active === entry.section ? "active" : ""}
            onClick={() => onChange(entry.section)}
          >
            <Icon aria-hidden={true} />
            <span>{entry.label}</span>
            {entry.meta && <small>{entry.meta}</small>}
          </button>
        );
      })}
    </nav>
  );
}

/**
 * One service in the Integrations table: its row (name, status, detail, one action), and
 * under it, while open, the keys, the test, the notes and the per-service setup.
 */
function IntegrationRow({
  provider,
  vaultReady,
  onChanged,
  focused,
}: {
  provider: Integration;
  vaultReady: boolean;
  onChanged: () => void;
  /** Opened from the overview (#integrations/<id>): expand this row and bring it into view. */
  focused: boolean;
}) {
  const [open, setOpen] = useState(focused);
  const card = useRef<HTMLTableRowElement>(null);
  const [connectionRevision, setConnectionRevision] = useState(0);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<"save" | "test" | "remove" | null>(null);
  const [notice, setNotice] = useState<{
    tone: "success" | "danger";
    text: string;
  } | null>(null);
  const Icon = iconMap[provider.icon] || Plug;
  const state = integrationState(provider);
  const vaultFields = provider.fields.filter(
    (field) => field.source === "vault",
  );
  const entered = Object.values(drafts).some((value) => value.trim());

  useEffect(() => {
    if (!focused) return;
    setOpen(true);
    const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    card.current?.scrollIntoView({ block: "start", behavior: still ? "auto" : "smooth" });
  }, [focused]);

  async function save() {
    const credentials = Object.fromEntries(
      Object.entries(drafts)
        .filter(([, value]) => value.trim())
        .map(([key, value]) => [key, value.trim()]),
    );
    if (!Object.keys(credentials).length) return;
    setBusy("save");
    setNotice(null);
    try {
      const result = await api<{ message: string }>(
        `/api/settings/integrations/${provider.id}`,
        {
          method: "PUT",
          body: JSON.stringify({ credentials }),
        },
      );
      setDrafts({});
      setConnectionRevision(v => v + 1);
      setNotice({ tone: "success", text: result.message });
      onChanged();
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          error instanceof Error
            ? error.message
            : "Credentials could not be saved.",
      });
    } finally {
      setBusy(null);
    }
  }

  async function test() {
    setBusy("test");
    setNotice(null);
    try {
      const result = await api<{ message: string }>(
        `/api/settings/integrations/${provider.id}/test`,
        { method: "POST" },
      );
      setNotice({ tone: "success", text: result.message });
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          error instanceof Error ? error.message : "Connection test failed.",
      });
    } finally {
      setBusy(null);
      // Either way the test is recorded: the status, Detail and Activity follow it.
      onChanged();
    }
  }

  async function remove() {
    if (
      !window.confirm(
        `Remove ${provider.name} credentials saved in the NOLA vault?`,
      )
    )
      return;
    setBusy("remove");
    setNotice(null);
    try {
      const result = await api<{ message: string }>(
        `/api/settings/integrations/${provider.id}`,
        { method: "DELETE" },
      );
      setNotice({ tone: "success", text: result.message });
      onChanged();
    } catch (error) {
      setNotice({
        tone: "danger",
        text:
          error instanceof Error
            ? error.message
            : "Credentials could not be removed.",
      });
    } finally {
      setBusy(null);
    }
  }

  const bodyId = `integration-${provider.id}-setup`;
  const detail = integrationDetail(provider);
  return (
    <>
      {/* The whole row opens and closes the setup; the button is what keyboards reach. */}
      <tr
        ref={card}
        id={`integration-${provider.id}`}
        className={`integration-row${open ? " open" : ""}`}
        onClick={() => setOpen((value) => !value)}
      >
        <td className="integration-name-cell">
          <div className="integration-ident">
            <span className="integration-icon">
              <Icon aria-hidden={true} />
            </span>
            <span className="integration-name">
              <strong>{provider.name}</strong>
              <small>{provider.description}</small>
            </span>
          </div>
        </td>
        <td className="integration-status-cell">
          <span className={`integration-status ${state.tone}`}>{state.text}</span>
        </td>
        <td className="integration-detail-cell" title={detail}>{detail}</td>
        <td className="integration-action-cell">
          <button
            type="button"
            className="button secondary integration-toggle"
            aria-expanded={open}
            aria-controls={bodyId}
            aria-label={`${open ? "Close" : provider.configured ? "Manage" : "Set up"} ${provider.name}`}
            onClick={(event) => {
              event.stopPropagation();
              setOpen((value) => !value);
            }}
          >
            {open ? "Close" : provider.configured ? "Manage" : "Set up"}
            <ChevronDown aria-hidden="true" />
          </button>
        </td>
      </tr>
      {open && (
        <tr className="integration-open-row" id={bodyId}>
          <td colSpan={4}>
            <div className="integration-body">
              {notice ? (
                <div
                  className={`integration-notice ${notice.tone}`}
                  role={notice.tone === "danger" ? "alert" : "status"}
                >
                  {notice.tone === "success" && <Check aria-hidden="true" />}
                  <span>{notice.text}</span>
                </div>
              ) : (
                provider.last_test && <LastTestLine test={provider.last_test} />
              )}
              <div className="credential-fields">
                {provider.fields.map((field) => (
                  <label className="credential-field" key={field.key}>
                    <span>
                      {field.label}
                      {field.required && <small>Required</small>}
                    </span>
                    <input
                      type={field.secret ? "password" : "text"}
                      value={drafts[field.key] || ""}
                      onChange={(event) =>
                        setDrafts((current) => ({
                          ...current,
                          [field.key]: event.target.value,
                        }))
                      }
                      placeholder={
                        field.configured
                          ? "Saved. Enter a new value to replace it"
                          : field.placeholder
                      }
                      autoComplete="off"
                      spellCheck={false}
                    />
                    {field.configured && (
                      <small className="credential-state">
                        {field.source === "vault"
                          ? "Saved in the encrypted vault. Never shown again."
                          : "Set on the server."}
                      </small>
                    )}
                  </label>
                ))}
              </div>
              {provider.id === "ghl" &&
                Number(provider.details.connected || 0) > 0 && (
                  <p className="integration-detail">
                    <Check aria-hidden="true" />
                    {String(provider.details.connected)} client token
                    {Number(provider.details.connected) === 1 ? "" : "s"} saved for
                    client CRM access.
                  </p>
                )}
              {(provider.id === "google_workspace" ||
                provider.id === "google_ads") &&
                Boolean(provider.details.oauth_connected) && (
                  <p className="integration-detail">
                    <Check aria-hidden="true" />
                    Google OAuth authorization is saved
                    {provider.details.account
                      ? ` as ${String(provider.details.account)}`
                      : ""}
                    .
                  </p>
                )}
              <div className="integration-actions">
                {provider.fields.length > 0 && <button
                  className="button primary"
                  disabled={!entered || !vaultReady || Boolean(busy)}
                  onClick={save}
                >
                  {busy === "save" ? "Saving…" : "Save credentials"}
                </button>}
                {provider.testable && (
                  <button
                    className="button secondary"
                    disabled={!provider.configured || Boolean(busy)}
                    onClick={test}
                  >
                    {busy === "test" ? "Testing…" : "Test connection"}
                  </button>
                )}
                {vaultFields.length > 0 && (
                  <button
                    className="button quiet-danger"
                    disabled={Boolean(busy)}
                    onClick={remove}
                  >
                    <Trash2 aria-hidden="true" />
                    {busy === "remove" ? "Removing…" : "Remove saved keys"}
                  </button>
                )}
              </div>
              {provider.id === "password_email" && provider.configured && <SendingDomain />}
              {provider.id === "google_workspace" && (
                <GoogleConnect
                  authorized={Boolean(provider.details.oauth_connected)}
                />
              )}
              {provider.id === "google_ads" && (
                <><p className="integration-note">
                  First authorize your account under Google Workspace, then save
                  your manager customer ID here and test access. Google Ads API access
                  belongs to the Cloud project behind your Google authorization; a developer token is no longer required.
                </p><GoogleAdsAccounts /></>
              )}
              {provider.id === "lsa" && (
                <p className="integration-note">
                  LSA uses the Google Workspace connection and refreshes access automatically.
                  Link the client’s Local Services Ads account in its Google Ads settings.
                  The connection test verifies shared authorization and manager access;
                  client account and recording permissions are checked during scans.
                  {Boolean(provider.details.scope_missing) && " An administrator must reauthorize Google Workspace to grant Ads access."}
                </p>
              )}
              {provider.id === "windsor" && <WindsorConnect key={connectionRevision} configured={provider.configured} />}
              {provider.id === "brightlocal" && (
                provider.configured ? <BrightLocalConnect /> : (
                  <p className="integration-note">
                    Save the API key here. Link each client's BrightLocal locations
                    on its Connections page.
                  </p>
                )
              )}
              {provider.id === "ghl" && (
                <ClientLinksNote what="GoHighLevel tokens are saved" />
              )}
              {!vaultReady && provider.fields.length > 0 && (
                <p className="vault-warning">
                  The server-side encryption key is missing. Add it to the
                  deployment before saving credentials.
                </p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function BasicSettings() {
  const { user } = useAuth();
  const health = useApi<HealthResponse>("/api/healthz");
  useScrollToMyGoogle(useLocation().hash);
  const connected =
    health.data?.status === "ok" && health.data.database === "connected";
  return (
    <>
      <PageHeader
        eyebrow="Workspace"
        title="Settings"
        description="Your account and workspace access."
      />
      <GoogleReturnNotice />
      <AppearanceSettings />
      <MyGoogle />
      <div className="settings-grid">
        <section className="panel settings-card">
          <div className="settings-icon">
            <UserRound aria-hidden="true" />
          </div>
          <div>
            <h2>Signed-in account</h2>
            <p>{user?.email}</p>
            <p className="settings-role">{roleLabel(user?.role)}</p>
          </div>
        </section>
        <section className="panel settings-card">
          <div className="settings-icon">
            <Database aria-hidden="true" />
          </div>
          <div>
            <h2>Workspace status</h2>
            <p>NOLA checks the production database live.</p>
            <StatusBadge tone={health.loading ? "neutral" : connected ? "success" : "danger"}>
              {health.loading ? "Checking" : connected ? "Connected" : "Unavailable"}
            </StatusBadge>
          </div>
        </section>
      </div>
    </>
  );
}

/** System & security: one status list, then the background jobs and the readiness evidence. */
function SecuritySection({ health, vaultReady, vaultLoading }: { health: Loaded<HealthResponse>; vaultReady: boolean; vaultLoading: boolean }) {
  const operations = useApi<Operations>("/api/operations");
  const connected = health.data?.status === "ok" && health.data.database === "connected";
  const workers = operations.data?.workers || [];
  const rows: { name: string; tone: Tone; value: string; detail?: string }[] = [
    health.loading && !health.data
      ? { name: "Database", tone: "neutral", value: "Checking" }
      : connected
        ? { name: "Database", tone: "success", value: "Connected" }
        : { name: "Database", tone: "danger", value: "Unavailable", detail: "NOLA cannot reach its database right now." },
    vaultLoading
      ? { name: "Credential vault", tone: "neutral", value: "Checking" }
      : vaultReady
        ? { name: "Credential vault", tone: "success", value: "Ready" }
        : { name: "Credential vault", tone: "danger", value: "Unavailable", detail: "The server is missing its encryption key, so keys cannot be saved." },
    operations.error
      ? { name: "Background jobs", tone: "danger", value: "Unavailable", detail: "Their status could not be loaded." }
      : !operations.data
        ? { name: "Background jobs", tone: "neutral", value: "Checking" }
        : workers.some((worker) => worker.status === "failed")
          ? { name: "Background jobs", tone: "danger", value: "Failed", detail: "See Worker activity below." }
          : workers.length
            ? { name: "Background jobs", tone: "success", value: "Running" }
            : { name: "Background jobs", tone: "neutral", value: "Not run yet" },
  ];
  return (
    <div className="settings-stack">
      <section className="panel settings-system">
        <div className="panel-heading"><h2>Status</h2></div>
        <ul className="settings-status-list">
          {rows.map((row) => (
            <li key={row.name}>
              <span className="settings-status-name">{row.name}</span>
              <span className="settings-status-value"><StatusBadge tone={row.tone}>{row.value}</StatusBadge></span>
              {row.detail && <span className="settings-status-detail">{row.detail}</span>}
            </li>
          ))}
        </ul>
        <p className="settings-panel-note">
          Only administrators can open these settings. Saved keys are encrypted and never shown again, and every sign-in is checked on each request.
        </p>
      </section>
      <WorkerStatus request={operations} />
      <OperationalReadiness />
    </div>
  );
}

/** One credential event in words: "API key saved", "Test failed: …". */
function describeEvent(event: CredentialEvent, fieldLabel: (key: string) => string) {
  if (event.action === "tested")
    return `Test ${event.outcome === "success" ? "passed" : "failed"}${event.detail ? `: ${event.detail}` : "."}`;
  if (event.detail) return event.detail;
  const verb = event.action === "configured" ? "saved" : event.action;
  return event.credential_key ? `${fieldLabel(event.credential_key)} ${verb}` : `Connection ${verb}`;
}

function AdminSettings() {
  const { user } = useAuth();
  const health = useApi<HealthResponse>("/api/healthz");
  const integrations = useApi<IntegrationResponse>(
    "/api/settings/integrations",
  );
  const events = useApi<CredentialEvent[]>("/api/settings/credential-events");
  const location = useLocation();
  const navigate = useNavigate();
  const [route, setRoute] = useState(() => parseHash(window.location.hash));
  useEffect(() => setRoute(parseHash(location.hash)), [location.hash]);
  useScrollToMyGoogle(location.hash);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");
  const { section, focus } = route;

  useEffect(() => {
    const sync = () => setRoute(parseHash(window.location.hash));
    window.addEventListener("hashchange", sync);
    return () => window.removeEventListener("hashchange", sync);
  }, []);
  // A card opened from the overview must not be hidden by an old search or category.
  useEffect(() => {
    if (!focus) return;
    setQuery("");
    setCategory("All");
  }, [focus]);

  function chooseSection(next: SettingsSection) {
    setRoute({ section: next, focus: null });
    window.scrollTo({ top: 0, behavior: "instant" });
    navigate(`${location.pathname}#${next}`, { replace: true });
  }
  function openIntegration(id: string) {
    setRoute({ section: "integrations", focus: id });
    navigate(`${location.pathname}#integrations/${id}`, { replace: true });
  }

  const providers = integrations.data?.providers || [];
  const categories = useMemo(
    () => Array.from(new Set(providers.map((item) => item.category))).sort((a, b) => categoryRank(a) - categoryRank(b)),
    [providers],
  );
  const filtered = providers.filter((provider) => {
    const matchesQuery =
      `${provider.name} ${provider.description} ${provider.category}`
        .toLowerCase()
        .includes(query.toLowerCase());
    return (
      matchesQuery && (category === "All" || provider.category === category)
    );
  });
  // The table: one group per category, in the shared order; services keep the catalog's order inside it.
  const groups = categories
    .map((name) => ({ name, items: filtered.filter((provider) => provider.category === name) }))
    .filter((group) => group.items.length);
  // Overview: what needs attention first (a failed test, then setup needed), then the rest.
  const byAttention = [...providers].sort((a, b) => integrationState(a).rank - integrationState(b).rank);
  const failing = providers.filter((provider) => integrationState(provider).rank === 0).length;
  const names = new Map(providers.map((provider) => [provider.id, provider.name]));
  const fieldLabels = new Map(providers.flatMap((provider) => provider.fields.map((field) => [field.key, field.label] as const)));
  const fieldLabel = (key: string) => fieldLabels.get(key) || sentence(key.toLowerCase());
  const connected =
    health.data?.status === "ok" && health.data.database === "connected";
  const copy = sectionCopy[section];

  return (
    <>
      {/* The one-line description sits beside the rail, not under the title: in the header it
          pushed the rail 21px down on some sections and not others. */}
      <PageHeader
        title={copy.title}
        actions={section === "overview" ? (
          <button className="button primary" onClick={() => chooseSection("integrations")}>
            <Plug aria-hidden="true" />
            Manage integrations
          </button>
        ) : undefined}
      />
      <div className="settings-workspace">
        <SettingsRail
          active={section}
          onChange={chooseSection}
          configured={integrations.data?.configured || 0}
          total={integrations.data?.total || 0}
        />
        <div className="settings-content">
          {copy.description && <p className="settings-section-note">{copy.description}</p>}
          <GoogleReturnNotice />
          {section === "appearance" && <AppearanceSettings />}
          {section === "overview" && (
            <div className="settings-stack">
              <MyGoogle />
              {/* The page header already names this view; its one action moved up there. */}
              <div className="settings-metrics">
                <article>
                  <span>
                    <Plug aria-hidden="true" />
                    Configured services
                  </span>
                  <strong>
                    {integrations.loading
                      ? "—"
                      : `${integrations.data?.configured || 0}/${integrations.data?.total || 0}`}
                  </strong>
                  <small className={failing ? "unhealthy" : undefined}>
                    {failing ? `${failing} failed ${failing === 1 ? "its" : "their"} last test` : "Have their keys saved"}
                  </small>
                </article>
                <article>
                  <span>
                    <Database aria-hidden="true" />
                    Production database
                  </span>
                  <strong className={connected ? "healthy" : "unhealthy"}>
                    {health.loading
                      ? "Checking"
                      : connected
                        ? "Online"
                        : "Needs attention"}
                  </strong>
                  <small>Supabase</small>
                </article>
                <article>
                  <span>
                    <LockKeyhole aria-hidden="true" />
                    Credential vault
                  </span>
                  <strong
                    className={
                      integrations.data?.vault_ready ? "healthy" : "unhealthy"
                    }
                  >
                    {integrations.loading
                      ? "Checking"
                      : integrations.data?.vault_ready
                        ? "Ready"
                        : "Unavailable"}
                  </strong>
                  <small>Saved keys are encrypted</small>
                </article>
              </div>
              <section className="panel settings-account-row">
                <span className="settings-icon">
                  <UserRound aria-hidden="true" />
                </span>
                <div>
                  <h2>{user?.name || "Workspace administrator"}</h2>
                  <p>{user?.email}</p>
                </div>
                <span className="settings-role">{roleLabel(user?.role)}</span>
              </section>
              <section className="panel settings-quick-list">
                <div className="panel-heading">
                  <div>
                    <h2>Integration readiness</h2>
                  </div>
                  <button
                    className="text-button"
                    onClick={() => chooseSection("integrations")}
                  >
                    View all{providers.length ? ` ${providers.length}` : ""}
                  </button>
                </div>
                {integrations.error ? (
                  <ErrorState message={integrations.error} onRetry={integrations.reload} />
                ) : !integrations.data ? (
                  <LoadingState label="Loading integrations" />
                ) : (
                  <div>
                    {byAttention.slice(0, 6).map((provider) => {
                      const Icon = iconMap[provider.icon] || Plug;
                      const state = integrationState(provider);
                      return (
                        <button
                          key={provider.id}
                          aria-label={`Open ${provider.name}: ${state.text}`}
                          onClick={() => openIntegration(provider.id)}
                        >
                          <span className="quick-provider-mark">
                            <Icon aria-hidden={true} />
                          </span>
                          <span>
                            <strong>{provider.name}</strong>
                            <small>
                              {state.rank === 0 && provider.last_test
                                ? `Failed ${formatDateTime(provider.last_test.at)}`
                                : provider.category}
                            </small>
                          </span>
                          <StatusBadge tone={state.tone}>{state.text}</StatusBadge>
                        </button>
                      );
                    })}
                  </div>
                )}
              </section>
            </div>
          )}

          {section === "integrations" && (
            <div className="settings-stack">
              {integrations.error ? (
                <ErrorState
                  message={integrations.error}
                  onRetry={integrations.reload}
                />
              ) : !integrations.data && integrations.loading ? (
                <LoadingState label="Loading integrations" />
              ) : (
                <>
                  <MyGoogleRow onOpen={() => navigate(`${location.pathname}#${MY_GOOGLE}`)} />
                  <div className="integration-toolbar">
                    <label className="settings-search">
                      <Search aria-hidden="true" />
                      <span className="sr-only">Search integrations</span>
                      <input
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        placeholder="Search integrations"
                      />
                    </label>
                    <FilterPill
                      label="Category"
                      value={category}
                      onChange={setCategory}
                      options={[
                        { value: "All", name: "All", count: providers.length },
                        ...categories.map((item) => ({
                          value: item,
                          name: item,
                          count: providers.filter((provider) => provider.category === item).length,
                        })),
                      ]}
                    />
                  </div>
                  <ClientLinksNote what="Google Ads and CTM accounts are linked" />
                  {groups.length > 0 && (
                    <section className="panel integration-panel" aria-label="Integrations">
                      <table className="integration-table">
                        <thead>
                          <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Status</th>
                            <th scope="col">Detail</th>
                            <th scope="col"><span className="sr-only">Action</span></th>
                          </tr>
                        </thead>
                        {groups.map((group) => (
                          <tbody key={group.name}>
                            <tr className="integration-group">
                              <th scope="colgroup" colSpan={4}>
                                {group.name}
                                <span>{group.items.length}</span>
                              </th>
                            </tr>
                            {group.items.map((provider) => (
                              <IntegrationRow
                                key={provider.id}
                                provider={provider}
                                focused={focus === provider.id}
                                vaultReady={Boolean(integrations.data?.vault_ready)}
                                onChanged={() => {
                                  integrations.reload();
                                  events.reload();
                                }}
                              />
                            ))}
                          </tbody>
                        ))}
                      </table>
                    </section>
                  )}
                  {!filtered.length && (
                    <div className="settings-empty">
                      <Search aria-hidden="true" />
                      <strong>No integrations found</strong>
                      <p>Try another search or category.</p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {section === "jarvis" && <JarvisSettings canEdit />}
          {section === "agent" && <AgentKeys />}

          {section === "members" && <MemberManagement />}

          {section === "changelog" && <section className="panel settings-changelog"><Markdown>{changelogText}</Markdown></section>}

          {section === "security" && (
            <SecuritySection
              health={health}
              vaultReady={Boolean(integrations.data?.vault_ready)}
              vaultLoading={!integrations.data && integrations.loading}
            />
          )}

          {section === "activity" && (
            <section className="panel settings-activity">
              <div className="panel-heading">
                <div>
                  <h2>Recent credential events</h2>
                </div>
                <button
                  className="icon-button"
                  aria-label="Refresh activity"
                  onClick={events.reload}
                >
                  <RefreshCw aria-hidden="true" />
                </button>
              </div>
              {events.loading ? (
                <LoadingState label="Loading activity" />
              ) : events.error ? (
                <ErrorState message={events.error} onRetry={events.reload} />
              ) : events.data?.length ? (
                // A dense log: one row per event, a failure is red text and nothing louder.
                <table className="activity-table">
                  <thead>
                    <tr>
                      <th scope="col">When</th>
                      <th scope="col">Service</th>
                      <th scope="col">Event</th>
                      <th scope="col">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {events.data.map((event) => {
                      const failed = event.outcome === "failed";
                      const text = describeEvent(event, fieldLabel);
                      return (
                        <tr key={event.id} className={failed ? "failed" : undefined}>
                          <td className="activity-when">
                            <time dateTime={event.occurred_at} title={formatDateTime(event.occurred_at)}>{shortWhen(event.occurred_at)}</time>
                          </td>
                          <td className="activity-service">
                            {names.get(event.provider) || sentence(event.provider)}
                            {event.client_name && <span className="activity-client"> for {event.client_name}</span>}
                          </td>
                          <td className="activity-event" title={text}>
                            <span className="activity-event-text">
                              {failed && !/fail/i.test(text) && <span className="sr-only">Failed: </span>}
                              {text}
                            </span>
                          </td>
                          <td className="activity-by">{event.actor_name || <span className="muted">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="settings-empty">
                  <Activity aria-hidden="true" />
                  <strong>No credential activity yet</strong>
                  <p>Saving or testing a key shows up here.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </div>
    </>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  return user?.role === "superadmin" ? <AdminSettings /> : <BasicSettings />;
}
